import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { barajar, urlImagen } from '../../../lib/formularios';
import { SinRespuesta } from './CamposTexto';

/*
 * Opción múltiple, casillas y desplegable.
 *
 * `marcas` (solo en lectura: calificación y vista individual) = { correctas: [ids] | null }.
 * Con `correctas` se pinta cada opción como en la revisión de Google: la elegida y buena en verde
 * con palomita, la elegida y mala en rojo con tache, y la buena que no se eligió, resaltada.
 */

function useOpciones(item, semilla) {
  return useMemo(
    () => (item.mezclar_opciones && semilla ? barajar(item.opciones || [], `${semilla}:${item.id}`) : (item.opciones || [])),
    [item.opciones, item.mezclar_opciones, item.id, semilla],
  );
}

function marcaDe(marcas, id, elegida) {
  const correctas = marcas?.correctas;
  if (!Array.isArray(correctas)) return '';
  const buena = correctas.includes(id);
  if (elegida && buena) return 'is-correcta';
  if (elegida && !buena) return 'is-incorrecta';
  if (!elegida && buena) return 'is-correcta-perdida';
  return '';
}

function IconoMarca({ clase }) {
  if (clase === 'is-correcta') return <Check className="fr-marca-icono is-bien" aria-label="Correcta" />;
  if (clase === 'is-incorrecta') return <X className="fr-marca-icono is-mal" aria-label="Incorrecta" />;
  return null;
}

function ImagenOpcion({ opcion }) {
  if (!opcion.imagen?.id) return null;
  return <img className="fr-opcion-imagen" src={urlImagen(opcion.imagen.id, API)} alt={opcion.texto || ''} loading="lazy" />;
}

export function CampoOpcionMultiple({ item, valor, onCambio, soloLectura, semilla, marcas, aria }) {
  const opciones = useOpciones(item, semilla);
  const grupo = `fr-om-${item.id}`;
  const elegida = valor?.opcion || null;
  const conOtro = !!valor && !valor.opcion && valor.otro !== undefined && valor.otro !== null;
  const conImagenes = opciones.some((o) => o.imagen?.id);

  return (
    <div className={`fr-opciones ${conImagenes ? 'con-imagenes' : ''}`} role="radiogroup" {...aria}>
      {opciones.map((o) => {
        const marca = soloLectura ? marcaDe(marcas, o.id, elegida === o.id) : '';
        return (
          <label key={o.id} className={`fr-opcion ${marca} ${elegida === o.id ? 'is-elegida' : ''}`}>
            <span className="fr-opcion-fila">
              <input
                type="radio"
                name={grupo}
                value={o.id}
                checked={elegida === o.id}
                disabled={soloLectura}
                onChange={() => onCambio({ opcion: o.id })}
              />
              <span className="fr-opcion-texto">{o.texto || 'Opción'}</span>
              <IconoMarca clase={marca} />
            </span>
            <ImagenOpcion opcion={o} />
          </label>
        );
      })}
      {item.otra && (
        <div className={`fr-opcion fr-opcion-otro ${conOtro ? 'is-elegida' : ''}`}>
          <span className="fr-opcion-fila">
            <input
              type="radio"
              name={grupo}
              checked={conOtro}
              disabled={soloLectura}
              onChange={() => onCambio({ otro: valor?.otro || '' })}
              aria-label="Otro"
            />
            <span className="fr-opcion-texto">Otro:</span>
            {soloLectura ? (
              <span className="fr-valor-leido fr-otro-leido">{conOtro ? valor.otro : ''}</span>
            ) : (
              <input
                className="fm-campo-linea fr-otro-texto"
                value={conOtro ? valor.otro : ''}
                onChange={(e) => onCambio({ otro: e.target.value })}
                aria-label="Otra respuesta"
              />
            )}
          </span>
        </div>
      )}
      {soloLectura && !valor && !marcas && <SinRespuesta />}
      {!soloLectura && !item.obligatoria && valor && (
        <div className="fr-borrar-seleccion">
          <button type="button" className="fm-boton-texto" onClick={() => onCambio(null)}>Borrar selección</button>
        </div>
      )}
    </div>
  );
}

export function CampoCasillas({ item, valor, onCambio, soloLectura, semilla, marcas, aria }) {
  const opciones = useOpciones(item, semilla);
  const elegidas = valor?.opciones || [];
  const otro = valor?.otro;
  const conOtro = otro !== undefined && otro !== null;
  const conImagenes = opciones.some((o) => o.imagen?.id);

  // Sin nada marcado la respuesta desaparece (así "obligatoria" funciona igual que en el servidor)
  const emitir = (ids, textoOtro) => {
    if (!ids.length && (textoOtro === null || textoOtro === undefined)) onCambio(null);
    else onCambio({ opciones: ids, otro: textoOtro === undefined ? null : textoOtro });
  };
  const alternar = (id) => {
    const ids = elegidas.includes(id) ? elegidas.filter((x) => x !== id) : [...elegidas, id];
    // Se conserva el orden de las opciones, no el de los clics: el resumen y la exportación lo agradecen
    const orden = (item.opciones || []).map((o) => o.id);
    emitir(ids.sort((a, b) => orden.indexOf(a) - orden.indexOf(b)), conOtro ? otro : null);
  };

  return (
    <div className={`fr-opciones ${conImagenes ? 'con-imagenes' : ''}`} role="group" {...aria}>
      {opciones.map((o) => {
        const marcada = elegidas.includes(o.id);
        const marca = soloLectura ? marcaDe(marcas, o.id, marcada) : '';
        return (
          <label key={o.id} className={`fr-opcion ${marca} ${marcada ? 'is-elegida' : ''}`}>
            <span className="fr-opcion-fila">
              <input type="checkbox" checked={marcada} disabled={soloLectura} onChange={() => alternar(o.id)} />
              <span className="fr-opcion-texto">{o.texto || 'Opción'}</span>
              <IconoMarca clase={marca} />
            </span>
            <ImagenOpcion opcion={o} />
          </label>
        );
      })}
      {item.otra && (
        <div className={`fr-opcion fr-opcion-otro ${conOtro ? 'is-elegida' : ''}`}>
          <span className="fr-opcion-fila">
            <input
              type="checkbox"
              checked={conOtro}
              disabled={soloLectura}
              onChange={() => emitir(elegidas, conOtro ? null : '')}
              aria-label="Otro"
            />
            <span className="fr-opcion-texto">Otro:</span>
            {soloLectura ? (
              <span className="fr-valor-leido fr-otro-leido">{conOtro ? otro : ''}</span>
            ) : (
              <input
                className="fm-campo-linea fr-otro-texto"
                value={conOtro ? otro : ''}
                onChange={(e) => emitir(elegidas, e.target.value)}
                aria-label="Otra respuesta"
              />
            )}
          </span>
        </div>
      )}
      {soloLectura && !valor && !marcas && <SinRespuesta />}
    </div>
  );
}

export function CampoDesplegable({ item, valor, onCambio, soloLectura, semilla, marcas, idCampo, aria }) {
  const opciones = useOpciones(item, semilla);
  const elegida = valor?.opcion || '';

  if (soloLectura) {
    const op = opciones.find((o) => o.id === elegida);
    const marca = marcaDe(marcas, elegida, true);
    return (
      <div className={`fr-valor-leido fr-desplegable-leido ${op ? marca : ''}`}>
        {op ? op.texto : <SinRespuesta />}
        {op && <IconoMarca clase={marca} />}
      </div>
    );
  }
  return (
    <div className="fr-desplegable">
      <select
        id={idCampo}
        className="fr-select"
        value={elegida}
        onChange={(e) => onCambio(e.target.value ? { opcion: e.target.value } : null)}
        {...aria}
      >
        <option value="">Elegir</option>
        {opciones.map((o) => <option key={o.id} value={o.id}>{o.texto || 'Opción'}</option>)}
      </select>
    </div>
  );
}
